// src/pages/Login.jsx
import { useState, useEffect } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import API from "../api";
import { showError } from "../utils/dialog";
import { isValidEmail } from "../utils/email";
import { setToken } from "../utils/auth";
import { getClientPublicIp } from "../utils/clientPublicIp";
import Swal from 'sweetalert2';
import LoginTwoFactorStep from "../components/twoFactor/LoginTwoFactorStep";
import talentLogo2 from "../assets/talentlogo2.png";
// import DebugAuth from "../components/DebugAuth";

export default function Login() {
  usePageMeta("Login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [returnUrl, setReturnUrl] = useState(null);
  const [twoFaChallenge, setTwoFaChallenge] = useState(null);

  // Check if already logged in - redirect to dashboard
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined') {
      console.log('Login: Already logged in, redirecting to dashboard');
      const userType = (localStorage.getItem('userType') || sessionStorage.getItem('userType') || '').toLowerCase();
      if (userType === 'fieldstaff' || userType === 'outsourcestaff') navigate('/availability');
      else if (userType === 'client' || userType === 'contact') navigate('/client-dashboard');
      else navigate('/dashboard');
    }
  }, [navigate]);

  // Check for return URL in query parameters
  useEffect(() => {
    const urlParam = searchParams.get('returnUrl');
    if (urlParam) {
      setReturnUrl(decodeURIComponent(urlParam));
      console.log('Redirect after login set to:', decodeURIComponent(urlParam));
    }
  }, [searchParams]);

  const processLoginSuccess = (res) => {
      console.log("Login response:", res.data);
      const userType = String(res.data.user?.type || '').toLowerCase();

      setToken(res.data.token);
      localStorage.setItem("accountId", res.data.user.account_id);
      localStorage.setItem("userType", userType);
      localStorage.setItem("isAdmin", userType === "admin" ? "true" : "false");
      localStorage.setItem("registrationComplete", res.data.user.registration ? "true" : "false");
      if (res.data.user.client_id) {
        localStorage.setItem("clientId", res.data.user.client_id);
      } else {
        localStorage.removeItem("clientId");
      }

      window.dispatchEvent(new Event('authUpdate'));

      Swal.fire({
        title: 'Success',
        text: res.data.message || "Login successful",
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });

      setTimeout(async () => {
        if (res.data.flag === 1) {
          localStorage.setItem("forcePasswordChange", "true");
          navigate("/change-password");
          return;
        }

        if (returnUrl && (returnUrl.includes('/checkin/scan') || returnUrl.includes('/checkin'))) {
          try {
            const base = window.location.origin;
            const parsed = new URL(returnUrl, base);
            const params = parsed.searchParams;
            const timesheetId = params.get('timesheetId');
            const from = params.get('from');
            const to = params.get('to');
            const client = params.get('client');
            const location = params.get('location');
            const accountId = res.data.user?.account_id;

            if (timesheetId) {
              const v = await API.get(`/api/timesheets/validate-access?timesheetId=${encodeURIComponent(timesheetId)}&accountId=${encodeURIComponent(accountId)}`);
              if (v.data && v.data.allowed) {
                navigate(returnUrl);
                return;
              }
              showError("You don't have shifts in this timesheet.");
              return;
            }

            const q = new URLSearchParams();
            if (from) q.set('from', from);
            if (to) q.set('to', to);
            if (client) q.set('client', client);
            if (location) q.set('location', location);

            if (q.toString()) {
              const ts = await API.get(`/api/timesheets?${q.toString()}`);
              const rows = (ts.data && ts.data.data) ? ts.data.data : ts.data || [];
              const allowed = new Set();
              (rows || []).forEach(r => {
                const candidate = r.account_id || r.staffId || r.staff_id || null;
                if (candidate) allowed.add(Number(candidate));
              });
              if (allowed.size === 0) {
                navigate(returnUrl);
                return;
              }
              if (allowed.has(Number(accountId))) {
                navigate(returnUrl);
                return;
              }
              showError("You don't have shifts in this timesheet.");
              return;
            }

            navigate(returnUrl);
            return;
          } catch (e) {
            console.error('Error validating checkin access after login:', e);
            showError('Unable to validate checkin access at this time.');
            return;
          }
        }

        const routingUserType = String(res.data.user?.type || '').toLowerCase();
        const registrationComplete = res.data.user?.registration === '1' || res.data.user?.registration === 1 || res.data.user?.registration_complete;

        if (routingUserType === 'fieldstaff' || routingUserType === 'outsourcestaff' || routingUserType === 'outsourcedstaff') {
          if (registrationComplete) {
            navigate("/availability");
          } else {
            navigate("/register");
          }
          return;
        }

        if (routingUserType === 'candidate') {
          if (res.data.alreadyRegistered) {
            Swal.fire({
              title: 'Already Registered',
              text: res.data.message,
              icon: 'info',
              confirmButtonText: 'OK',
              allowOutsideClick: false,
            }).then((result) => {
              if (result.isConfirmed) navigate("/signup");
            });
            return;
          }
          if (!registrationComplete) {
            navigate("/register");
            return;
          }
          Swal.fire({
            title: 'Registration Complete',
            text: 'You have already completed registration. Please login to continue.',
            icon: 'info',
            confirmButtonText: 'OK',
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) navigate("/signup");
          });
          return;
        }

        const clientId = res.data.user.client_id;
        const isClientDashboardUser = clientId &&
          routingUserType !== 'fieldstaff' &&
          routingUserType !== 'outsourcestaff';

        if (isClientDashboardUser) {
          navigate("/client-dashboard");
          return;
        }

        switch (routingUserType) {
          case 'admin':
          case 'officestaff':
          case 'manager':
            navigate("/dashboard");
            break;
          default:
            if (!registrationComplete) {
              navigate("/register");
            } else {
              navigate("/dashboard");
            }
        }
      }, 100);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!isValidEmail(email)) {
      showError("Please enter a valid email address");
      return;
    }

    try {
      setIsSubmitting(true);
      const clientIp = await getClientPublicIp();
      const res = await API.post(
        "/auth/login",
        { email, password, clientIp },
        { validateStatus: (status) => status < 500 },
      );

      if (res.status === 496 && res.data?.requires2fa) {
        setTwoFaChallenge({
          pendingToken: res.data.pendingToken,
          verificationMethod: res.data.verificationMethod,
        });
        setIsSubmitting(false);
        return;
      }

      if (res.status !== 200 || !res.data?.token) {
        throw Object.assign(new Error(res.data?.message || "Login failed"), {
          response: res,
        });
      }

      setTwoFaChallenge(null);
      processLoginSuccess(res);
      setIsSubmitting(false);
    } catch (err) {
      const data = err.response?.data;
      console.error("Login error:", err);
      console.error("Error response:", data);

      let message = data?.message || "Login failed";
      if (!err.response && err.code === "ERR_NETWORK") {
        message =
          "Cannot reach the login server. Check backend status and CORS/API URL settings.";
      }

      showError(message);
      setIsSubmitting(false);

      // If backend gives a redirect in error (like 403 for reset)
      if (data?.redirect) {
        navigate(data.redirect);
      }
    }
  };

  return (
    <div className="auth-container">
      {/* <DebugAuth /> */}
      <div className="background-overlay"></div>
      <div className="form-container">
        <div className="form-card">
          <div className="atlas-logo">
            <div className="flex items-start gap-6">
              <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
            </div>
          </div>

          <h1 className="form-title">Sign In</h1>

          {twoFaChallenge ? (
            <LoginTwoFactorStep
              pendingToken={twoFaChallenge.pendingToken}
              verificationMethod={twoFaChallenge.verificationMethod}
              onCancel={() => setTwoFaChallenge(null)}
              onSuccess={(data) => {
                setIsSubmitting(false);
                processLoginSuccess({ data });
              }}
            />
          ) : (
          <>
          {returnUrl && returnUrl.includes('/checkin') && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-800">
                <div className="font-semibold">Authentication Required</div>
                <div className="text-xs">Please sign in to continue with face recognition check-in.</div>
              </div>
            </div>
          )}

          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email*</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password*</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={isSubmitting}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`submit-button ${isSubmitting ? "loading" : ""}`}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  <span>Signing In...</span>
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="form-footer">
            Don&apos;t have an account yet? <Link className="toggle-form" to="/signup">Sign up</Link>
          </p>
          <p className="form-footer">
            Forgot your password? <Link className="toggle-form" to="/forgot-password">Reset it</Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
